import React from 'react';
import withShipment from '../../../withShipment';

import SearchableTable from '../../global/Search/SearchableTable';
import SaveIcon from '@material-ui/icons/Save';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import {
  ENABLE_MATCH_RULE,
  MANAGE_RULE,
  RULE_DESCRIPTION,
  SAVE,
  ENABLE_NFC_TICKET_OUT,
  NFC_TICKET_OUT_DESCRIPTION,
  ENABLE_AUDIT_VERIFICATION,
  AUDIT_VERIFICATION_DESCRIPTION,
  ENABLE_TICKET_PHOTO,
  TICKET_PHOTO_DESCRIPTION,
  ENABLE_VIP_MATCH,
  VIP_MATCH_DESCRIPTION,
  VIP_MATCH_POINT_PRESETS,
  DEFAULT_VIP_MATCH_POINTS,
  REQUIRE_PHONE_VERIFICATION,
  PHONE_VERIFICATION_DESCRIPTION,
  ENABLE_NUVE_BOOST,
  NUVE_BOOST_DESCRIPTION,
} from './constants';
import { userInfoSelector } from '../../../redux/selectors/auth';
import { TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { listRules, saveRules } from '../../../redux/actions/settings';
import { refreshUserInfo } from '../../../redux/actions/auth';
import { clearStatus } from '../../../redux/actions/global';
import { rulesSelector } from '../../../redux/selectors/settings';
import {
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
} from '../../../redux/actions/ticketOut';
import { ticketPhotoSettingSelector } from '../../../redux/selectors/ticketOut';
import {
  DEFAULT_TICKET_PHOTO_SETTING,
  TICKET_THRESHOLD_PRESETS,
} from '../../ticketOut/constants';
import './ManageRule.css';

class ManageRule extends SearchableTable {
  constructor(props) {
    super(props);
    this.state = {
      machines: this.props.machines || [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      enableMatchRule: false,
      ruleName: '',
      timeLimit: '',
      maxMatchesPerDay: '',
      maxCountResetTime: '',
      maxCountReset: '',
      // NFC ticket-out is on by default; the toggle only appears when
      // the owning company has the NFC feature enabled.
      nfcTicketOutEnabled: true,
      companyNfcEnabled: false,
      enableAuditVerification: false,
      ticketPhotoEnabled: DEFAULT_TICKET_PHOTO_SETTING.enabled,
      ticketPhotoThreshold: String(
        DEFAULT_TICKET_PHOTO_SETTING.thresholdAmount
      ),
      vipMatchEnabled: false,
      vipMatchPoints: String(DEFAULT_VIP_MATCH_POINTS),
      // Phone verification is required by default; admins opt a
      // location out by unchecking this.
      requirePhoneVerification: true,
      // NuVe Boost menu visibility, location-level. Enabled by default so
      // Boost-eligible locations keep the menu until an admin opts out. The
      // toggle only renders when the company's plan includes Boost
      // (companyBoostEligible).
      enableNuveBoost: true,
      companyBoostEligible: false,
    };
  }

  componentDidMount() {
    this.props.listRules({
      locationId: this.props.userInfo.selectedLocationId,
    });
    this.props.getTicketPhotoSetting({
      locationId: this.props.userInfo.selectedLocationId,
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.rules !== this.props.rules) {
      if (Array.isArray(this.props.rules) && this.props.rules.length > 0) {
        const rule = this.props.rules[0];
        this.setState({
          ruleName: rule.ruleName,
          timeLimit: rule.timeLimit,
          enableMatchRule: rule.enableMatchRule,
          maxMatchesPerDay: rule.maxMatchesPerDay,
          resetTimes: rule.resetTimes,
          maxCountResetTime: rule.maxCountResetTime,
          nfcTicketOutEnabled: rule.nfcTicketOutEnabled !== false,
          companyNfcEnabled: rule.companyNfcEnabled === true,
          enableAuditVerification: rule.enableAuditVerification === true,
          vipMatchEnabled: rule.vipMatchEnabled === true,
          vipMatchPoints:
            rule.vipMatchPoints && rule.vipMatchPoints !== ''
              ? String(rule.vipMatchPoints)
              : String(DEFAULT_VIP_MATCH_POINTS),
          requirePhoneVerification: rule.requirePhoneVerification !== false,
          enableNuveBoost: rule.enableNuveBoost !== false,
          companyBoostEligible: rule.companyBoostEligible === true,
        });
      }
    }

    if (
      prevProps.ticketPhotoSetting !== this.props.ticketPhotoSetting &&
      this.props.ticketPhotoSetting
    ) {
      const setting = this.props.ticketPhotoSetting;
      this.setState({
        ticketPhotoEnabled:
          setting.enabled != null
            ? setting.enabled
            : DEFAULT_TICKET_PHOTO_SETTING.enabled,
        ticketPhotoThreshold:
          setting.thresholdAmount != null
            ? String(setting.thresholdAmount)
            : String(DEFAULT_TICKET_PHOTO_SETTING.thresholdAmount),
      });
    }
  }

  getContextualMenuItems = () => [
    {
      title: SAVE,
      icon: <SaveIcon />,
      onClick: this.handleSave,
      disabled: this.isSaveDisabled(),
    },
  ];

  handleSave = () => {
    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', error);
      } else {
        this.openFeedbackBar('success', response);
        // Re-fetch the user so a flipped NuVe Boost rule shows/hides its
        // sidebar item immediately — no re-login or location switch needed.
        this.props.refreshUserInfo();
      }
    };

    this.props.saveRules(
      {
        ruleName: this.state.ruleName,
        locationId: this.props.userInfo.selectedLocationId,
        enableMatchRule: this.state.enableMatchRule,
        timeLimit: this.state.timeLimit,
        resetTimes: this.state.resetTimes,
        maxMatchesPerDay: this.state.maxMatchesPerDay,
        maxCountResetTime: this.state.maxCountResetTime,
        // Only the company-allowed NFC value is meaningful; when the
        // company has NFC disabled this falls back to the stored default.
        nfcTicketOutEnabled: this.state.nfcTicketOutEnabled,
        enableAuditVerification: this.state.enableAuditVerification,
        vipMatchEnabled: this.state.vipMatchEnabled,
        vipMatchPoints: this.state.vipMatchPoints,
        requirePhoneVerification: this.state.requirePhoneVerification,
        enableNuveBoost: this.state.enableNuveBoost,
      },
      callback
    );

    this.props.saveTicketPhotoSetting({
      locationId: this.props.userInfo.selectedLocationId,
      enabled: this.state.ticketPhotoEnabled,
      thresholdAmount: Number(this.state.ticketPhotoThreshold || 0),
    });
  };

  handleTicketThresholdChange = (event) => {
    const { value } = event.target;
    if (value === '' || /^[0-9]+$/.test(value)) {
      this.setState({ ticketPhotoThreshold: value });
    }
  };

  setTicketThresholdPreset = (value) => {
    this.setState({ ticketPhotoThreshold: String(value) });
  };

  handleVipPointsChange = (event) => {
    const { value } = event.target;
    if (value === '' || /^[0-9]+$/.test(value)) {
      this.setState({ vipMatchPoints: value });
    }
  };

  setVipPointsPreset = (value) => {
    this.setState({ vipMatchPoints: String(value) });
  };

  isSaveDisabled = () => {
    return this.state.machines.some((template) => !template.machine);
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value });
  };

  // Toggle card styled to match Manage Company's "Enable NFC Ticket-Out"
  // control. `name` is the state key it drives.
  renderToggleCard = (name, label, hint) => {
    const on = !!this.state[name];
    return (
      <label className={`rule-toggle-card ${on ? 'rule-toggle-card--on' : ''}`}>
        <span className="rule-checkbox">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => this.setState({ [name]: e.target.checked })}
          />
          <span className="rule-checkbox__box" aria-hidden="true">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 8.5 6.5 12 13 4.5" />
            </svg>
          </span>
        </span>
        <span className="rule-toggle-text">
          <span className="rule-toggle-label">{label}</span>
          <span className="rule-toggle-hint">{hint}</span>
        </span>
      </label>
    );
  };

  render() {
    return (
      <React.Fragment>
        <TopToolbar
          pageName={MANAGE_RULE}
          menuItems={this.getContextualMenuItems()}
        />

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <div className="manage-rule-container">
          <div className="manage-rule-form-section">
            <div
              className={`rule-match-section ${
                this.state.enableMatchRule ? 'rule-match-section--on' : ''
              }`}
            >
              {this.renderToggleCard(
                'enableMatchRule',
                ENABLE_MATCH_RULE,
                RULE_DESCRIPTION
              )}

              {this.state.enableMatchRule && (
                <div className="form-fields rule-match-fields">
                  <TextField
                    className="input-manage-text-field"
                    name="ruleName"
                    label="Rule Name"
                    value={this.state.ruleName}
                    onChange={this.handleChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                  <TextField
                    className="input-manage-text-field"
                    label="Cooldown (hours)"
                    name="timeLimit"
                    value={this.state.timeLimit}
                    InputLabelProps={{ shrink: true }}
                    onChange={this.handleChange}
                    variant="outlined"
                    type="number"
                  />

                  <TextField
                    className="input-manage-text-field"
                    label="Max Matches per Day"
                    name="maxMatchesPerDay"
                    InputLabelProps={{ shrink: true }}
                    value={this.state.maxMatchesPerDay}
                    onChange={this.handleChange}
                    type="number"
                    variant="outlined"
                  />
                  <TextField
                    className="input-manage-text-field"
                    label="Reset Times (comma-separated, e.g. 05:00,17:00)"
                    name="resetTimes"
                    value={this.state.resetTimes}
                    InputLabelProps={{ shrink: true }}
                    onChange={this.handleChange}
                    variant="outlined"
                  />

                  <TextField
                    className="input-manage-text-field"
                    label="Reset Day Time (e.g. 05:00)"
                    name="maxCountResetTime"
                    value={this.state.maxCountResetTime}
                    InputLabelProps={{ shrink: true }}
                    onChange={this.handleChange}
                    variant="outlined"
                  />
                </div>
              )}
            </div>

            <div className="rule-toggle-group">
              {this.state.companyNfcEnabled &&
                this.renderToggleCard(
                  'nfcTicketOutEnabled',
                  ENABLE_NFC_TICKET_OUT,
                  NFC_TICKET_OUT_DESCRIPTION
                )}

              {this.renderToggleCard(
                'enableAuditVerification',
                ENABLE_AUDIT_VERIFICATION,
                AUDIT_VERIFICATION_DESCRIPTION
              )}

              {this.renderToggleCard(
                'requirePhoneVerification',
                REQUIRE_PHONE_VERIFICATION,
                PHONE_VERIFICATION_DESCRIPTION
              )}

              {this.state.companyBoostEligible &&
                this.renderToggleCard(
                  'enableNuveBoost',
                  ENABLE_NUVE_BOOST,
                  NUVE_BOOST_DESCRIPTION
                )}
            </div>

            <div
              className={`rule-match-section ${
                this.state.ticketPhotoEnabled ? 'rule-match-section--on' : ''
              }`}
            >
              {this.renderToggleCard(
                'ticketPhotoEnabled',
                ENABLE_TICKET_PHOTO,
                TICKET_PHOTO_DESCRIPTION
              )}

              {this.state.ticketPhotoEnabled && (
                <div className="form-fields rule-match-fields rule-ticket-photo-fields">
                  <TextField
                    className="input-manage-text-field"
                    label="Minimum Amount"
                    name="ticketPhotoThreshold"
                    value={this.state.ticketPhotoThreshold}
                    onChange={this.handleTicketThresholdChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    type="tel"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: '0',
                      step: '1',
                    }}
                  />
                  <div className="rule-preset-pills">
                    {TICKET_THRESHOLD_PRESETS.map((preset) => (
                      <button
                        type="button"
                        key={preset}
                        className={
                          'rule-preset-pill' +
                          (Number(this.state.ticketPhotoThreshold) === preset
                            ? ' active'
                            : '')
                        }
                        onClick={() => this.setTicketThresholdPreset(preset)}
                      >
                        ${preset.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className={`rule-match-section ${
                this.state.vipMatchEnabled ? 'rule-match-section--on' : ''
              }`}
            >
              {this.renderToggleCard(
                'vipMatchEnabled',
                ENABLE_VIP_MATCH,
                VIP_MATCH_DESCRIPTION
              )}

              {this.state.vipMatchEnabled && (
                <div className="form-fields rule-match-fields rule-ticket-photo-fields">
                  <TextField
                    className="input-manage-text-field"
                    label="Minimum Points"
                    name="vipMatchPoints"
                    value={this.state.vipMatchPoints}
                    onChange={this.handleVipPointsChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    type="tel"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: '0',
                      step: '1',
                    }}
                  />
                  <div className="rule-preset-pills">
                    {VIP_MATCH_POINT_PRESETS.map((preset) => (
                      <button
                        type="button"
                        key={preset}
                        className={
                          'rule-preset-pill' +
                          (Number(this.state.vipMatchPoints) === preset
                            ? ' active'
                            : '')
                        }
                        onClick={() => this.setVipPointsPreset(preset)}
                      >
                        {preset.toLocaleString()} pts
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  rules: rulesSelector(state),
  ticketPhotoSetting: ticketPhotoSettingSelector(state),
});

const actionCreators = {
  listRules,
  saveRules,
  clearStatus,
  getTicketPhotoSetting,
  saveTicketPhotoSetting,
  refreshUserInfo,
};
export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  ManageRule
);
